<template>
  <div class="chart-container">
    <Bar
      :data="chartData"
      :options="chartOptions"
      :redraw="true"
      style="width: 100%; height: 100%;"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from '@/services/axios'
import { Bar } from 'vue-chartjs'
import {
  Chart, BarElement, CategoryScale, LinearScale,
  Title, Tooltip, Legend
} from 'chart.js'

Chart.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend)

const props = defineProps({
  dataInicio: { type: String, required: true },
  dataFim:    { type: String, required: true }
})

const chartData = ref({ labels: [], datasets: [] })

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  // ← define aqui como o tooltip se comporta
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    title: {
      display: true,
      text: 'Receitas Totais por Mês',
      align: 'start'
    },
    legend: { display: false },
    tooltip: {
      // callbacks apenas para formatação, sem mode/intersect aqui
      callbacks: {
        // cada segmento formatado como R$
        label(ctx) {
          const val = ctx.parsed.y
          return `${ctx.dataset.label}: ${val.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
          })}`
        },
        // total do stack
        footer(ctxs) {
          const chart = ctxs[0].chart
          const idx   = ctxs[0].dataIndex
          const total = chart.data.datasets
            .reduce((sum, ds) => sum + ds.data[idx], 0)
          return `Total: ${total.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
          })}`
        }
      },
      footerFont: { weight: 'bold' }
    }
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        callback(value) {
          return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          })
        }
      }
    }
  }
}

async function fetchData(){
  try {
    const { data: resp } = await axios.get(
    '/gestao/graficos/individuais/receitas-totais-mensal',
      { params: { dataInicio: props.dataInicio, dataFim: props.dataFim } }
    )
   const monthMap = {
     Jan: 'Jan', Feb: 'Fev', Mar: 'Mar', Apr: 'Abr',
     May: 'Mai', Jun: 'Jun', Jul: 'Jul', Aug: 'Ago',
     Sep: 'Set', Oct: 'Out', Nov: 'Nov', Dec: 'Dez'
   }
   // Transforma labels Mon/YYYY → MêsPT/YYYY
   const labelsPT = resp.labels.map(lbl => {
     const [mon, year] = lbl.split('/');
     const pt = monthMap[mon] || mon;
     return `${pt}/${year}`;
   })

    chartData.value = {
      labels: labelsPT,
      datasets: [
        {
          label: 'Matrículas',
          data: resp.series.matriculas,
          backgroundColor: '#4A90E2'
        },
        {
          label: 'Mensalidades',
          data: resp.series.mensalidades,
          backgroundColor: '#1ABC9C'
        },
        {
          label: 'Aulas Avulsas',
          data: resp.series.aulasAvulsas,
          backgroundColor: '#F5A623'
        },
        {
          label: 'Aulas Experimentais',
          data: resp.series.aulasExperimentais,
          backgroundColor: '#E74C3C'
        },
        {
          label: 'Vendas da Loja',
          data: resp.series.vendasLoja,
          backgroundColor: '#9B59B6'
        },
        {
          label: 'Receitas Gerais',
          data: resp.series.receitasGerais,
          backgroundColor: '#34495E'
        }
      ]
    }
  } catch (e) {
    console.error('Erro ao carregar receitas totais mensal:', e)
  }
}

onMounted(fetchData)
watch(() => [props.dataInicio, props.dataFim], fetchData)
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
}
@media (max-width: 767px) {
  .chart-container {
    height: 300px;
  }
}
</style>
